import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Search, Trash2, Home, Filter, X, Loader2, FileText, Building, CheckCircle, Clock, ChevronLeft, ChevronRight, Plus, Split, Euro, RotateCcw } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

// Etichette parlanti per i tipi documento
const TIPO_DOC_LABELS = {
  FV: { label: "Fatt. Vendita", short: "FV", color: "bg-blue-50 text-blue-700 border-blue-200" },
  FA: { label: "Fatt. Acquisto", short: "FA", color: "bg-slate-50 text-slate-700 border-slate-200" },
  FVE: { label: "Fatt. Vendita Est.", short: "FVE", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  FAE: { label: "Fatt. Acquisto Est.", short: "FAE", color: "bg-zinc-50 text-zinc-700 border-zinc-200" },
  NCV: { label: "Nota Cred. Vendita", short: "NCV", color: "bg-orange-50 text-orange-700 border-orange-200" },
  NCA: { label: "Nota Cred. Acquisto", short: "NCA", color: "bg-amber-50 text-amber-700 border-amber-200" },
  NCVE: { label: "N.C. Vendita Est.", short: "NCVE", color: "bg-orange-50 text-orange-700 border-orange-200" },
  NCAE: { label: "N.C. Acquisto Est.", short: "NCAE", color: "bg-amber-50 text-amber-700 border-amber-200" },
};
const getTipoDocInfo = (t) => TIPO_DOC_LABELS[t] || { label: t || "-", short: t || "-", color: "bg-gray-50 text-gray-700 border-gray-200" };
const isNotaCreditoAcq = (t) => t === "NCA" || t === "NCAE";

export default function BollettePage() {
  const { t } = useTranslation();
  const [bollette, setBollette] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStato, setFilterStato] = useState("all");
  const [appartamenti, setAppartamenti] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Dialog states
  const [assignDialog, setAssignDialog] = useState({ open: false, bolletta: null });
  const [selectedAppartamento, setSelectedAppartamento] = useState("");
  const [assignDateInizio, setAssignDateInizio] = useState("");
  const [assignDateFine, setAssignDateFine] = useState("");
  const [assignAccountId, setAssignAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, bolletta: null });
  
  // Allocazione multipla
  const [multiMode, setMultiMode] = useState(false);
  const [multiAllocazioni, setMultiAllocazioni] = useState([]);
  const [savingMulti, setSavingMulti] = useState(false);
  
  // Column filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterFornitore, setFilterFornitore] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterTipoDoc, setFilterTipoDoc] = useState("all");

  // Bollette Manuali
  const [bolletteManuali, setBolletteManuali] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [manualDialog, setManualDialog] = useState({ open: false, bolletta: null });
  const [manualForm, setManualForm] = useState({
    fornitore: "", descrizione: "", importo: "", data: "", data_scadenza: "",
    note: "", tipo_bolletta: "bolletta", numero_fattura: ""
  });
  const [savingManual, setSavingManual] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [bollRes, bmRes, appRes, accRes, ccRes] = await Promise.all([
        api.get(`${prefix}/bollette`),
        api.get(`${prefix}/bollette-manuali`).catch(() => ({ data: [] })),
        api.get(`${prefix}/appartamenti`).catch(() => ({ data: [] })),
        api.get(`${prefix}/accounts`).catch(() => ({ data: [] })),
        api.get(`${prefix}/cost-centers`).catch(() => ({ data: [] })),
      ]);
      setBollette(bollRes.data);
      setBolletteManuali(bmRes.data || []);
      setAppartamenti(appRes.data || []);
      setAccounts(accRes.data || []);
      setCostCenters(ccRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadBills"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const openManualDialog = (b) => {
    if (b) {
      setManualForm({
        fornitore: b.fornitore || "",
        descrizione: b.descrizione || "",
        importo: String(b.importo || ""),
        data: b.data || "",
        data_scadenza: b.data_scadenza || "",
        note: b.note || "",
        tipo_bolletta: b.tipo_bolletta || "bolletta",
        numero_fattura: b.numero_fattura || "",
      });
      setManualDialog({ open: true, bolletta: b });
    } else {
      setManualForm({
        fornitore: "", descrizione: "", importo: "", data: "", data_scadenza: "",
        note: "", tipo_bolletta: "bolletta", numero_fattura: ""
      });
      setManualDialog({ open: true, bolletta: null });
    }
  };

  const handleSaveManual = async () => {
    if (!manualForm.importo || parseFloat(manualForm.importo) <= 0) {
      handleApiError({ message: "L'importo è obbligatorio e maggiore di zero" });
      return;
    }
    setSavingManual(true);
    try {
      const prefix = getApiPrefix();
      const payload = {
        ...manualForm,
        importo: parseFloat(manualForm.importo),
      };
      if (manualDialog.bolletta?.id) {
        await api.put(`${prefix}/bollette-manuali/${manualDialog.bolletta.id}`, payload);
        showSuccess("Bolletta manuale aggiornata");
      } else {
        await api.post(`${prefix}/bollette-manuali`, payload);
        showSuccess("Bolletta manuale creata");
      }
      setManualDialog({ open: false, bolletta: null });
      await fetchData();
    } catch (err) {
      handleApiError(err, "Errore salvataggio bolletta manuale");
    } finally {
      setSavingManual(false);
    }
  };

  const handleDeleteManual = async (b) => {
    if (!window.confirm(`Eliminare la bolletta manuale ${b.fornitore || b.descrizione || ''}?`)) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/bollette-manuali/${b.id}`);
      showSuccess("Bolletta eliminata");
      await fetchData();
    } catch (err) {
      handleApiError(err, "Errore eliminazione bolletta");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset dialog state quando si apre
  const openAssignDialog = (bolletta) => {
    setSelectedAppartamento(bolletta.appartamento_id || "");
    setAssignDateInizio(bolletta.data_inizio_competenza ? bolletta.data_inizio_competenza.split("T")[0] : "");
    setAssignDateFine(bolletta.data_fine_competenza ? bolletta.data_fine_competenza.split("T")[0] : "");
    setAssignAccountId(bolletta.conto_id || "");
    setMultiMode(false);
    setMultiAllocazioni([]);
    setAssignDialog({ open: true, bolletta });
  };

  // Aggiungi riga allocazione multipla
  const addMultiRow = () => {
    setMultiAllocazioni([...multiAllocazioni, { appartamento_id: "", importo: "", note: "", data_inizio_competenza: assignDateInizio || "", data_fine_competenza: assignDateFine || "" }]);
  };

  // Rimuovi riga allocazione multipla
  const removeMultiRow = (index) => {
    setMultiAllocazioni(multiAllocazioni.filter((_, i) => i !== index));
  };

  // Aggiorna riga allocazione multipla
  const updateMultiRow = (index, field, value) => {
    const updated = [...multiAllocazioni];
    updated[index] = { ...updated[index], [field]: value };
    setMultiAllocazioni(updated);
  };

  // Calcola totale allocazioni multiple
  const getMultiTotal = () => {
    return multiAllocazioni.reduce((sum, a) => sum + (parseFloat(a.importo) || 0), 0);
  };

  // Calcola importo bolletta totale (con IVA indeducibile)
  const getBollettaTotal = () => {
    if (!assignDialog.bolletta) return 0;
    return (assignDialog.bolletta.importo_totale || assignDialog.bolletta.importo || 0);
  };

  // Verifica se le allocazioni sono valide
  const isMultiValid = () => {
    if (multiAllocazioni.length < 2) return false;
    const hasEmpty = multiAllocazioni.some(a => !a.appartamento_id || !a.importo);
    if (hasEmpty) return false;
    const diff = Math.abs(getMultiTotal() - getBollettaTotal());
    return diff < 0.01;
  };

  // Assegnazione singola
  const handleAssignAppartamento = async () => {
    if (!selectedAppartamento) {
      handleApiError({ message: t("errors.requiredSelectApartment") });
      return;
    }
    try {
      const prefix = getApiPrefix();
      const isManuale = assignDialog.bolletta?.source === 'manuale';
      const url = isManuale
        ? `${prefix}/bollette-manuali/${assignDialog.bolletta.id}/assegna-appartamento`
        : `${prefix}/bollette/${assignDialog.bolletta.id}/assegna-appartamento`;
      await api.put(url, {
        appartamento_id: selectedAppartamento,
        data_inizio_competenza: assignDateInizio || null,
        data_fine_competenza: assignDateFine || null,
        account_id: (assignAccountId && assignAccountId !== "none") ? assignAccountId : null
      });
      showSuccess(t("success.apartmentAssigned"));
      setAssignDialog({ open: false, bolletta: null });
      setSelectedAppartamento("");
      // Forza il ricaricamento dei dati
      await fetchData();
    } catch (err) {
      
      handleApiError(err, t("errors.errorAssignment"));
    }
  };

  // Reset allocazione
  const handleResetAllocazione = async () => {
    if (!assignDialog.bolletta) return;
    if (!window.confirm("Resettare l'allocazione? Tutte le sub-allocazioni saranno fuse in un'unica riga 'in attesa'.")) return;
    try {
      const prefix = getApiPrefix();
      const res = await api.post(`${prefix}/bollette/${assignDialog.bolletta.id}/reset`);
      showSuccess(`Allocazione resettata - ${res.data?.righe_eliminate || 0} righe rimosse, totale ${res.data?.totale_ripristinato || 0}€ ripristinato`);
      setAssignDialog({ open: false, bolletta: null });
      setMultiAllocazioni([]);
      setMultiMode(false);
      fetchData();
    } catch (err) {
      handleApiError(err, "Errore reset allocazione");
    }
  };

  // Allocazione multipla
  const handleMultiAssign = async () => {
    if (!isMultiValid()) {
      handleApiError({ message: t("errors.verifyFieldsAndTotal") });
      return;
    }
    setSavingMulti(true);
    try {
      const prefix = getApiPrefix();
      const isManuale = assignDialog.bolletta?.source === 'manuale';
      const url = isManuale
        ? `${prefix}/bollette-manuali/${assignDialog.bolletta.id}/allocazione-multipla`
        : `${prefix}/bollette/${assignDialog.bolletta.id}/allocazione-multipla`;
      await api.post(url, {
        allocazioni: multiAllocazioni.map(a => ({
          appartamento_id: a.appartamento_id,
          importo: parseFloat(a.importo),
          note: a.note || null,
          data_inizio_competenza: a.data_inizio_competenza || null,
          data_fine_competenza: a.data_fine_competenza || null
        }))
      });
      showSuccess(`Bolletta allocata su ${multiAllocazioni.length} appartamenti`);
      setAssignDialog({ open: false, bolletta: null });
      setMultiAllocazioni([]);
      setMultiMode(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorMultiAllocation"));
    } finally {
      setSavingMulti(false);
    }
  };

  const handleDelete = async () => {
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/bollette/${deleteDialog.bolletta.id}`);
      showSuccess(t("success.billDeleted"));
      setDeleteDialog({ open: false, bolletta: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const formatCurrency = (val) => {
    if (val == null) return "-";
    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return dateStr;
  };

  // Filter logic
  const filteredBollette = bollette.filter((b) => {
    const matchSearch = !search || 
      b.fattura_numero?.toLowerCase().includes(search.toLowerCase()) ||
      b.fattura_fornitore?.toLowerCase().includes(search.toLowerCase()) ||
      b.descrizione?.toLowerCase().includes(search.toLowerCase());
    
    const matchStato = filterStato === "all" || b.stato_sotto_allocazione === filterStato;
    
    const matchFornitore = !filterFornitore || 
      b.fattura_fornitore?.toLowerCase().includes(filterFornitore.toLowerCase());
    
    const matchTipoDoc = filterTipoDoc === "all" || b.tipo_documento === filterTipoDoc;
    
    return matchSearch && matchStato && matchFornitore && matchTipoDoc;
  });

  // Pagination
  const totalPages = Math.ceil(filteredBollette.length / itemsPerPage);
  const paginatedBollette = filteredBollette.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats
  const stats = {
    totale: bollette.length,
    inAttesa: bollette.filter(b => b.stato_sotto_allocazione === 'in_attesa').length,
    completate: bollette.filter(b => b.stato_sotto_allocazione === 'completata').length,
    importoTotale: bollette.reduce((sum, b) => sum + (b.importo_totale || b.importo || 0), 0)
  };

  const getAppartamentoName = (id) => {
    const app = appartamenti.find(a => a.id === id);
    return app?.nome || "-";
  };

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
  }

  return (
    <div className="space-y-6" data-testid="bollette-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("bills.title")}</h1>
          <p className="text-muted-foreground text-sm">Gestione allocazioni bollette su appartamenti</p>
        </div>
        <Button onClick={() => openManualDialog(null)} data-testid="new-bolletta-manuale-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuova Bolletta Manuale
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale Bollette</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tracking-tight">{stats.totale}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('common.scheduled')}</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.inAttesa}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('common.completed')}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.completate}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Importo Totale</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tracking-tight">{formatCurrency(stats.importoTotale)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per numero fattura, fornitore..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={filterStato} onValueChange={setFilterStato}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-stato">
            <SelectValue placeholder={t("common.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="in_attesa">{t('common.scheduled')}</SelectItem>
            <SelectItem value="completata">{t('common.completed')}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? "bg-muted" : ""}
        >
          <Filter className="h-4 w-4 mr-1" />{t('common.filters')}</Button>
      </div>

      {/* Column Filters */}
      {showFilters && (
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>{t('common.supplier')}</Label>
              <Input
                placeholder="Filtra per fornitore..."
                value={filterFornitore}
                onChange={(e) => setFilterFornitore(e.target.value)}
              />
            </div>
            <div>
              <Label>Tipo Documento</Label>
              <Select value={filterTipoDoc} onValueChange={setFilterTipoDoc}>
                <SelectTrigger data-testid="filter-tipo-documento">
                  <SelectValue placeholder="Tutti i tipi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="FV">Fatt. Vendita (FV)</SelectItem>
                  <SelectItem value="FA">Fatt. Acquisto (FA)</SelectItem>
                  <SelectItem value="FVE">Fatt. Vendita Est. (FVE)</SelectItem>
                  <SelectItem value="FAE">Fatt. Acquisto Est. (FAE)</SelectItem>
                  <SelectItem value="NCV">Nota Cred. Vendita (NCV)</SelectItem>
                  <SelectItem value="NCA">Nota Cred. Acquisto (NCA)</SelectItem>
                  <SelectItem value="NCVE">N.C. Vendita Est. (NCVE)</SelectItem>
                  <SelectItem value="NCAE">N.C. Acquisto Est. (NCAE)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data da</Label>
              <DateInput
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
              />
            </div>
            <div>
              <Label>Data a</Label>
              <DateInput
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilterFornitore("");
                setFilterDateFrom("");
                setFilterDateTo("");
                setFilterTipoDoc("all");
              }}
            >
              <X className="h-4 w-4 mr-1" /> Resetta filtri
            </Button>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('invoices.title')}</TableHead>
                <TableHead>Tipo Doc.</TableHead>
                <TableHead>{t('common.supplier')}</TableHead>
                <TableHead>{t("common.date")}</TableHead>
                <TableHead>{t('movements.account')}</TableHead>
                <TableHead className="text-right">{t("common.amount")}</TableHead>
                <TableHead>Competenza</TableHead>
                <TableHead>{t('apartments.title')}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedBollette.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    {bollette.length === 0 
                      ? "Nessuna bolletta da allocare. Assegna allocazioni di tipo 'Bollette' dalla pagina Fatture."
                      : "Nessun risultato trovato"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBollette.map((b) => {
                  const tipoInfo = getTipoDocInfo(b.tipo_documento);
                  return (
                  <TableRow key={b.id}>
                    <TableCell>
                      <div className="font-medium">{b.fattura_numero || "-"}</div>
                      {b.invoice_id_riferimento && b.fattura_riferimento_numero && (
                        <div className="text-xs text-amber-700 flex items-center gap-1">
                          ↳ Rif. Fatt. {b.fattura_riferimento_numero}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${tipoInfo.color}`} data-testid={`tipo-doc-${b.id}`}>
                        {tipoInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{b.fattura_fornitore || "-"}</TableCell>
                    <TableCell>{formatDate(b.fattura_data)}</TableCell>
                    <TableCell>{b.conto_nome || "-"}</TableCell>
                    <TableCell className="text-right font-mono">
                      <div>
                        <div>{formatCurrency(b.importo_totale || b.importo)}</div>
                        {b.iva_indeducibile > 0 && (
                          <div className="text-xs text-orange-600">
                            (incl. IVA ind. {formatCurrency(b.iva_indeducibile)})
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {b.data_inizio_competenza && b.data_fine_competenza 
                        ? `${b.data_inizio_competenza} → ${b.data_fine_competenza}`
                        : b.data_inizio_competenza || "-"}
                    </TableCell>
                    <TableCell>
                      {b.appartamento_id ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          {getAppartamentoName(b.appartamento_id)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {b.stato_sotto_allocazione === 'in_attesa' ? (
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-700">{t('common.scheduled')}</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-100 text-green-700">
                          Completata
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Pulsante per assegnare o modificare appartamento */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAssignDialog(b)}
                          title={b.appartamento_id ? "Modifica appartamento" : "Assegna appartamento"}
                          className={b.appartamento_id ? "text-orange-600 hover:text-orange-700" : "text-blue-600 hover:text-blue-700"}
                        >
                          <Home className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, bolletta: b })}
                          title={t("common.delete")}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Pagina {currentPage} di {totalPages} ({filteredBollette.length} bollette)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Sezione Bollette Manuali */}
      {bolletteManuali.length > 0 && (
        <Card data-testid="bollette-manuali-section">
          <CardHeader>
            <CardTitle className="text-lg">Bollette Manuali</CardTitle>
            <p className="text-xs text-muted-foreground">
              Bollette create direttamente qui (non derivate da fatture). Vengono chiuse collegandole a un movimento bancario nel dialog di allocazione con tipo &quot;bolletta&quot;.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fornitore</TableHead>
                  <TableHead>N. Fattura</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead>Appartamento</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bolletteManuali.map(b => (
                  <TableRow key={b.id} data-testid={`bolletta-manuale-row-${b.id}`}>
                    <TableCell className="font-medium">{b.fornitore || '-'}</TableCell>
                    <TableCell className="text-sm">{b.numero_fattura || '-'}</TableCell>
                    <TableCell className="text-sm">{b.descrizione || '-'}</TableCell>
                    <TableCell>{b.data || '-'}</TableCell>
                    <TableCell>{b.data_scadenza || '-'}</TableCell>
                    <TableCell>{b.appartamento_nome || '-'}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(b.importo)}</TableCell>
                    <TableCell>
                      {b.stato_pagamento === 'pagata' ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200">Pagata</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">Non pagata</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openAssignDialog({
                        // Adatta bolletta manuale al formato atteso dal dialog Assegna Appartamento
                        id: b.id,
                        source: 'manuale',
                        fattura_numero: b.descrizione || '(bolletta manuale)',
                        fattura_fornitore: b.fornitore,
                        fattura_data: b.data,
                        tipo_documento: null,
                        tipo_allocazione: b.tipo_bolletta === 'manutenzione' ? 'manutenzioni_appartamenti' : 'bollette',
                        importo: b.importo,
                        iva_indeducibile: 0,
                        appartamento_id: b.appartamento_id,
                        conto_id: b.conto_id,
                        data_inizio_competenza: b.data_inizio_competenza,
                        data_fine_competenza: b.data_fine_competenza,
                        stato_sotto_allocazione: b.stato_pagamento === 'pagata' ? 'confermata' : 'in_attesa',
                      })} disabled={b.stato_pagamento === 'pagata'} title="Assegna Appartamento" data-testid={`assign-manual-${b.id}`}>
                        <Home className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openManualDialog(b)} disabled={b.stato_pagamento === 'pagata'} title="Modifica" data-testid={`edit-manual-${b.id}`}>
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteManual(b)} disabled={!!b.bank_movement_id} title="Elimina" data-testid={`delete-manual-${b.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Assign Appartamento Dialog - con supporto allocazione multipla */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => { 
        setAssignDialog({ ...assignDialog, open }); 
        if (!open) { setMultiMode(false); setMultiAllocazioni([]); setSelectedAppartamento(""); }
      }}>
        <DialogContent className={`${multiMode ? "max-w-6xl" : "max-w-4xl"} max-h-[90vh] overflow-y-auto`} data-testid="assign-appartamento-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {multiMode ? (
                <>
                  <Split className="h-5 w-5 text-purple-600" />
                  Allocazione Multipla
                </>
              ) : (
                <>
                  <Home className="h-5 w-5" />
                  {assignDialog.bolletta?.appartamento_id ? t("apartments.editApartment") : t("apartments.assignApartment")}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {multiMode 
                ? "Suddividi l'importo della bolletta su più appartamenti"
                : assignDialog.bolletta?.tipo_allocazione === 'manutenzioni_appartamenti' 
                  ? "Seleziona l'appartamento per questa manutenzione"
                  : "Seleziona l'appartamento a cui assegnare questa bolletta"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Info bolletta */}
            <div className="bg-muted p-3 rounded-md space-y-1">
              <p className="text-sm"><span className="text-muted-foreground">Fattura:</span> {assignDialog.bolletta?.fattura_numero}</p>
              <p className="text-sm"><span className="text-muted-foreground">Fornitore:</span> {assignDialog.bolletta?.fattura_fornitore}</p>
              <div className="text-sm flex items-center flex-wrap gap-1">
                <span className="text-muted-foreground">Tipo Documento:</span>
                {assignDialog.bolletta?.tipo_documento && (
                  <Badge variant="outline" className={`ml-1 text-xs ${getTipoDocInfo(assignDialog.bolletta.tipo_documento).color}`}>
                    {getTipoDocInfo(assignDialog.bolletta.tipo_documento).label}
                  </Badge>
                )}
                <Badge variant="outline" className="ml-1">
                  {assignDialog.bolletta?.tipo_allocazione === 'manutenzioni_appartamenti' ? '🔧 Manutenzione' : '⚡ Bolletta'}
                </Badge>
              </div>
              <p className="text-sm font-semibold">
                <span className="text-muted-foreground">Importo Totale:</span>{" "}
                <span className="text-blue-700">{formatCurrency(getBollettaTotal())}</span>
                {assignDialog.bolletta?.iva_indeducibile > 0 && (
                  <span className="text-xs text-orange-600 ml-2">
                    (incl. IVA ind. {formatCurrency(assignDialog.bolletta?.iva_indeducibile)})
                  </span>
                )}
              </p>
            </div>

            {/* Toggle allocazione multipla */}
            {!assignDialog.bolletta?.appartamento_id && (
              <div className="flex items-center justify-between p-3 border rounded-lg bg-purple-50/50">
                <div className="flex items-center gap-2">
                  <Split className="h-4 w-4 text-purple-600" />
                  <Label htmlFor="multi-mode" className="text-sm font-medium cursor-pointer">
                    Allocazione su più appartamenti
                  </Label>
                </div>
                <Switch 
                  id="multi-mode" 
                  checked={multiMode} 
                  onCheckedChange={(checked) => {
                    setMultiMode(checked);
                    if (checked && multiAllocazioni.length === 0) {
                      // Inizializza con 2 righe vuote
                      setMultiAllocazioni([
                        { appartamento_id: "", importo: "", note: "" },
                        { appartamento_id: "", importo: "", note: "" }
                      ]);
                    }
                  }}
                />
              </div>
            )}
            
            {/* Modalità singola */}
            {!multiMode && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Appartamento *</Label>
                  <SearchableSelect
                    options={appartamenti.map(app => ({ value: app.id, label: `${app.nome}${app.indirizzo ? ` - ${app.indirizzo}` : ""}${app.citta ? ` (${app.citta})` : ""}` }))}
                    value={selectedAppartamento}
                    onValueChange={setSelectedAppartamento}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("employees.searchApartment")}
                    testId="select-appartamento"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Data Inizio Competenza</Label>
                    <DateInput value={assignDateInizio} onChange={e => setAssignDateInizio(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Data Fine Competenza</Label>
                    <DateInput value={assignDateFine} onChange={e => setAssignDateFine(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('movements.account')}</Label>
                  <SearchableSelect
                    options={[
                      { value: "none", label: "-- Nessuno --" },
                      ...accounts.map(acc => ({ value: acc.id, label: `${acc.codice} - ${acc.nome}` }))
                    ]}
                    value={assignAccountId || "none"}
                    onValueChange={setAssignAccountId}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    emptyMessage="Nessun conto trovato"
                  />
                </div>
              </div>
            )}

            {/* Modalità multipla */}
            {multiMode && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Suddivisione importi</Label>
                  <Button variant="outline" size="sm" onClick={addMultiRow}>
                    <Plus className="h-4 w-4 mr-1" />{t('common.add')}</Button>
                </div>
                
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {multiAllocazioni.map((alloc, index) => (
                    <div key={`alloc-${index}`} className="p-3 border rounded-md bg-card space-y-2">
                      <div className="flex gap-2 items-start">
                        <div className="flex-1">
                          <SearchableSelect
                            options={appartamenti.map(app => ({ value: app.id, label: app.nome || app.indirizzo }))}
                            value={alloc.appartamento_id}
                            onValueChange={(v) => updateMultiRow(index, 'appartamento_id', v)}
                            placeholder="Appartamento..."
                            searchPlaceholder={t("employees.searchApartment")}
                            testId={`select-appartamento-multi-${index}`}
                          />
                        </div>
                        <div className="w-[140px]">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={t("common.amount")}
                            value={alloc.importo}
                            onChange={(e) => updateMultiRow(index, 'importo', e.target.value)}
                            className="h-9 font-mono"
                            data-testid={`alloc-importo-${index}`}
                          />
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-9 px-2 text-red-500"
                          onClick={() => removeMultiRow(index)}
                          disabled={multiAllocazioni.length <= 2}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1">
                          <Label className="text-xs text-muted-foreground">Data Inizio Competenza</Label>
                          <DateInput
                            value={alloc.data_inizio_competenza || ""}
                            onChange={(e) => updateMultiRow(index, 'data_inizio_competenza', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-muted-foreground">Data Fine Competenza</Label>
                          <DateInput
                            value={alloc.data_fine_competenza || ""}
                            onChange={(e) => updateMultiRow(index, 'data_fine_competenza', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs text-muted-foreground">Note (opzionale)</Label>
                          <Input
                            placeholder={t("common.notes")}
                            value={alloc.note}
                            onChange={(e) => updateMultiRow(index, 'note', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Riepilogo totali */}
                <div className={`p-3 rounded-md ${isMultiValid() ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Totale allocato:</span>
                    <span className={`font-mono font-bold ${isMultiValid() ? 'text-green-700' : 'text-yellow-700'}`}>
                      {formatCurrency(getMultiTotal())}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-muted-foreground">Importo bolletta:</span>
                    <span className="font-mono font-bold text-blue-700">
                      {formatCurrency(getBollettaTotal())}
                    </span>
                  </div>
                  {!isMultiValid() && Math.abs(getMultiTotal() - getBollettaTotal()) > 0.01 && (
                    <div className="mt-2 text-xs text-yellow-700">
                      Differenza: {formatCurrency(Math.abs(getMultiTotal() - getBollettaTotal()))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {assignDialog.bolletta?.appartamento_id && (
              <Button variant="outline" onClick={handleResetAllocazione} className="mr-auto text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="btn-reset-allocazione">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset Allocazione
              </Button>
            )}
            <Button variant="outline" onClick={() => { 
              setAssignDialog({ open: false, bolletta: null }); 
              setSelectedAppartamento(""); 
              setMultiMode(false);
              setMultiAllocazioni([]);
            }}>{t('common.cancel')}</Button>
            {multiMode ? (
              <Button onClick={handleMultiAssign} disabled={!isMultiValid() || savingMulti}>
                {savingMulti ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Split className="h-4 w-4 mr-2" />}
                Alloca su {multiAllocazioni.length} appartamenti
              </Button>
            ) : (
              <Button onClick={handleAssignAppartamento} disabled={!selectedAppartamento}>
                <Home className="h-4 w-4 mr-2" />
                Assegna
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questa bolletta? L&apos;allocazione sulla fattura verrà rimossa.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted p-3 rounded-md">
            <p className="text-sm">Fattura: {deleteDialog.bolletta?.fattura_numero}</p>
            <p className="text-sm">Importo: {formatCurrency(deleteDialog.bolletta?.importo)}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, bolletta: null })}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuova/Modifica Bolletta Manuale */}
      <Dialog open={manualDialog.open} onOpenChange={(open) => setManualDialog({ ...manualDialog, open })}>
        <DialogContent className="max-w-lg" data-testid="manual-bolletta-dialog">
          <DialogHeader>
            <DialogTitle>{manualDialog.bolletta ? 'Modifica' : 'Nuova'} Bolletta Manuale</DialogTitle>
            <DialogDescription>
              Bolletta creata manualmente (non da fattura). La scadenza comparirà nello Scadenziario finché non viene pagata tramite un movimento bancario. L&apos;allocazione ad appartamento avviene poi dal dialog standard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Fornitore *</Label>
              <Input value={manualForm.fornitore} onChange={(e) => setManualForm({...manualForm, fornitore: e.target.value})} placeholder="es. Enel, Sorgenia, ..." data-testid="manual-fornitore" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input value={manualForm.descrizione} onChange={(e) => setManualForm({...manualForm, descrizione: e.target.value})} placeholder="es. Bolletta gas dicembre 2025" data-testid="manual-descrizione" />
            </div>
            <div>
              <Label>N. Fattura</Label>
              <Input value={manualForm.numero_fattura} onChange={(e) => setManualForm({...manualForm, numero_fattura: e.target.value})} placeholder="es. FT-2026-001" data-testid="manual-numero-fattura" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Importo *</Label>
                <Input type="number" step="0.01" min="0" value={manualForm.importo} onChange={(e) => setManualForm({...manualForm, importo: e.target.value})} data-testid="manual-importo" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={manualForm.tipo_bolletta} onValueChange={(v) => setManualForm({...manualForm, tipo_bolletta: v})}>
                  <SelectTrigger data-testid="manual-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bolletta">Bolletta</SelectItem>
                    <SelectItem value="manutenzione">Manutenzione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data</Label>
                <Input value={manualForm.data} onChange={(e) => setManualForm({...manualForm, data: e.target.value})} placeholder="DD-MM-YYYY" data-testid="manual-data" />
              </div>
              <div>
                <Label>Data Scadenza</Label>
                <Input value={manualForm.data_scadenza} onChange={(e) => setManualForm({...manualForm, data_scadenza: e.target.value})} placeholder="DD-MM-YYYY" data-testid="manual-data-scadenza" />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Input value={manualForm.note} onChange={(e) => setManualForm({...manualForm, note: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialog({ open: false, bolletta: null })}>Annulla</Button>
            <Button onClick={handleSaveManual} disabled={savingManual} data-testid="save-manual-btn">
              {savingManual && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {manualDialog.bolletta ? 'Salva Modifiche' : 'Crea Bolletta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
